/**
 * Shared UI Components
 * Reusable form elements and layout components
 */

import React from 'react';

export const TabBar = ({ tabs, activeTab, onTabChange }) => (
  <div className="flex border-b border-slate-700 mb-6 overflow-x-auto">
    {tabs.map(tab => (
      <button
        key={tab.id}
        onClick={() => onTabChange(tab.id)}
        className={`px-6 py-3 font-medium text-sm whitespace-nowrap transition-colors ${
          activeTab === tab.id
            ? 'text-amber-500 border-b-2 border-amber-500 bg-slate-800/50'
            : 'text-slate-400 hover:text-white hover:bg-slate-800/30'
        }`}
      >
        {tab.label}
      </button>
    ))}
  </div>
);

export const Card = ({ title, children, action }) => (
  <div className="bg-slate-800 rounded-lg border border-slate-700 mb-6">
    {title && (
      <div className="flex justify-between items-center px-4 py-3 border-b border-slate-700">
        <h3 className="font-semibold text-white">{title}</h3>
        {action}
      </div>
    )}
    <div className="p-4">{children}</div>
  </div>
);

export const Button = ({ 
  children, 
  onClick, 
  variant = 'primary', 
  size = 'md', 
  disabled = false,
  className = ''
}) => {
  const base = 'font-medium rounded-lg transition-colors disabled:opacity-50';
  const sizes = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2',
    lg: 'px-6 py-3 text-lg'
  };
  const variants = {
    primary: 'bg-amber-500 hover:bg-amber-600 text-slate-900',
    secondary: 'bg-slate-700 hover:bg-slate-600 text-white',
    danger: 'bg-red-600 hover:bg-red-700 text-white'
  };
  
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${base} ${sizes[size]} ${variants[variant]} ${className}`}
    >
      {children}
    </button>
  );
};

export const EditableCell = ({ 
  value, 
  onChange, 
  type = 'number', 
  prefix = '', 
  suffix = '', 
  highlight = false 
}) => (
  <div className={`relative ${highlight ? 'bg-amber-500/20' : ''}`}>
    {prefix && (
      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
        {prefix}
      </span>
    )}
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
      className={`w-full bg-transparent border-0 text-white text-right py-2 px-2 focus:outline-none focus:ring-1 focus:ring-amber-500 rounded ${
        prefix ? 'pl-6' : ''
      } ${suffix ? 'pr-8' : ''}`}
    />
    {suffix && (
      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
        {suffix}
      </span>
    )}
  </div>
);

export const Select = ({ 
  label, 
  value, 
  onChange, 
  options, 
  placeholder = '-- Select --', 
  disabled = false 
}) => (
  <div>
    {label && <label className="block text-slate-400 text-sm mb-2">{label}</label>}
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value || null)}
      disabled={disabled}
      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-50"
    >
      <option value="">{placeholder}</option>
      {options.map(opt => (
        <option key={opt.id || opt.value} value={opt.id || opt.value}>
          {opt.label || opt.name}
        </option>
      ))}
    </select>
  </div>
);

export const Checkbox = ({ label, checked, onChange, disabled = false }) => (
  <label className={`flex items-center gap-3 cursor-pointer ${disabled ? 'opacity-50' : ''}`}>
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      disabled={disabled}
      className="w-5 h-5 rounded border-slate-600 bg-slate-700 text-amber-500 focus:ring-amber-500"
    />
    <span className="text-white">{label}</span>
  </label>
);

export const NumberInput = ({ label, value, onChange, placeholder, min, max, hint }) => (
  <div>
    {label && <label className="block text-slate-400 text-sm mb-2">{label}</label>}
    <input
      type="number"
      value={value || ''}
      onChange={(e) => onChange(parseInt(e.target.value) || 0)}
      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white"
      placeholder={placeholder}
      min={min}
      max={max}
    />
    {hint && <p className="text-slate-500 text-xs mt-1">{hint}</p>}
  </div>
);
